import * as React from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment/InputAdornment";
import {
  CatalogAddress,
  formatCatalogAddress,
  getCatalogAddresses,
  getDefaultCatalogAddressId,
} from "../../../../../services/addressService";

export type AddressStatus = "loading" | "ready" | "error" | "empty" | "seattleDefault";

interface IProps {
  data?: any;
  isAmazonCustomer?: any;
  isDemographicProfile?: any;
  onAddressReadyChange?: (ready: boolean) => void;
}

interface IState {
  addresses: CatalogAddress[];
  addressStatus: AddressStatus;
  residentialAddressId: string;
  emailPrefix: any;
  name: any;
  prime: any;
  rxPassForceActive: any;
  rxPassSubscription: any;
  creditCard: any;
  giftCard: any;
  giftCardBalance: any;
  giftCardBalanceTextField: any;
  giftCardBalanceTextColor: any;
  phone: any;
  hornbillSubscription: any;
}

class BasicFieldsSection extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      addresses: [],
      addressStatus: "loading",
      residentialAddressId: "",
      creditCard: true,
      emailPrefix: "",
      giftCard: false,
      giftCardBalance: 50.0,
      giftCardBalanceTextColor: "gray",
      giftCardBalanceTextField: true,
      name: "",
      phone: "",
      prime: false,
      rxPassForceActive: false,
      rxPassSubscription: false,
      hornbillSubscription: false,
    };

    this.handleName = this.handleName.bind(this);
    this.handleEmailPrefix = this.handleEmailPrefix.bind(this);
    this.handlePrime = this.handlePrime.bind(this);
    this.handleRxPass = this.handleRxPass.bind(this);
    this.handleRxPassForceActive = this.handleRxPassForceActive.bind(this);
    this.handleCreditCard = this.handleCreditCard.bind(this);
    this.handleGiftCard = this.handleGiftCard.bind(this);
    this.handleGiftCardBalance = this.handleGiftCardBalance.bind(this);
    this.handlePhoneNumber = this.handlePhoneNumber.bind(this);
    this.handleHornbillSubscription = this.handleHornbillSubscription.bind(this);
    this.handleResidentialAddressId = this.handleResidentialAddressId.bind(this);
    this.getAddresses = this.getAddresses.bind(this);
    this.useSeattleDefault = this.useSeattleDefault.bind(this);
  }

  public componentDidMount() {
    // Pharmacy flows pick the address in DemographicProfileFieldsSection instead.
    if (this.props.isAmazonCustomer) {
      this.getAddresses();
    }
  }

  public getAddresses() {
    this.setAddressStatus("loading");
    getCatalogAddresses()
      .then((addresses) => {
        this.setState({
          addresses,
          residentialAddressId: getDefaultCatalogAddressId(addresses),
        });
        this.setAddressStatus(addresses.length > 0 ? "ready" : "empty");
      })
      .catch((error) => {
        console.error(error);
        this.setAddressStatus("error");
      });
  }

  // Blank residentialAddressId makes the backend use its Seattle default.
  public useSeattleDefault() {
    this.setState({ residentialAddressId: "" });
    this.setAddressStatus("seattleDefault");
  }

  private setAddressStatus(addressStatus: AddressStatus) {
    this.setState({ addressStatus });
    this.props.onAddressReadyChange?.(
      addressStatus === "ready" || addressStatus === "seattleDefault"
    );
  }

  private renderAddressField() {
    const { addressStatus } = this.state;
    if (addressStatus === "error" || addressStatus === "empty") {
      return (
        <FormControl style={{ width: 225 }}>
          <FormHelperText error>
            {addressStatus === "error"
              ? "Couldn't load addresses."
              : "No addresses available."}
          </FormHelperText>
          <Button size="small" color="primary" onClick={this.getAddresses}>
            Retry
          </Button>
          <Button size="small" onClick={this.useSeattleDefault}>
            Use Seattle default
          </Button>
        </FormControl>
      );
    }
    if (addressStatus === "seattleDefault") {
      return (
        <FormControl style={{ width: 225 }}>
          <FormHelperText>Using Seattle default address.</FormHelperText>
          <Button size="small" color="primary" onClick={this.getAddresses}>
            Retry loading addresses
          </Button>
        </FormControl>
      );
    }
    const loading = addressStatus === "loading";
    return (
      <FormControl disabled={loading}>
        <InputLabel>{loading ? "Loading addresses..." : "Address"}</InputLabel>
        <Select
          value={loading ? "" : this.state.residentialAddressId}
          style={{ width: 225 }}
          onChange={this.handleResidentialAddressId}
          IconComponent={
            loading ? () => <CircularProgress size={16} aria-label="Loading addresses" /> : undefined
          }
        >
          {this.state.addresses.map((address) => (
            <MenuItem key={address.addressId} value={address.addressId}>
              <em>{formatCatalogAddress(address)}</em>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  public render() {
    return (
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        spacing={3}
      >
        <Grid item xs>
          <InputLabel
            style={{
              color: "red",
              fontSize: 18,
              fontWeight: "bold",
            }}
          >
            Basic Profile
          </InputLabel>
        </Grid>

        <Grid item xs>
          <TextField
            style={{ width: 225 }}
            label="Name (Optional)"
            variant="outlined"
            value={this.state.name}
            onChange={this.handleName}
          />
        </Grid>

        <Grid item xs>
          <TextField
            style={{ width: 225 }}
            label="Email Prefix (Optional)"
            variant="outlined"
            value={this.state.emailPrefix}
            onChange={this.handleEmailPrefix}
          />
        </Grid>

        <Grid item xs>
          <TextField
            style={{ width: 225 }}
            label="Phone Number (Optional)"
            helperText="Strips special characters and only keeps the last 10 digits"
            variant="outlined"
            value={this.state.phone}
            onChange={this.handlePhoneNumber}
            inputProps={{
              maxLength: 15,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">+1</InputAdornment>
              ),
            }}
          />
        </Grid>

        {this.props.isAmazonCustomer && (
          <Grid item xs>
            {this.renderAddressField()}
          </Grid>
        )}

        <Grid item xs>
          <FormControl component="fieldset">
            <FormGroup style={{ width: 225 }}>
              <FormControlLabel
                control={<Checkbox checked={this.state.prime} />}
                label="Add Prime Membership"
                onChange={this.handlePrime}
              />
            </FormGroup>
          </FormControl>
        </Grid>

        <Grid item xs>
          <FormControl component="fieldset">
            <FormGroup style={{ width: 225 }}>
              <FormControlLabel
                control={<Checkbox checked={this.state.creditCard} />}
                label="Add Credit Card"
                onChange={this.handleCreditCard}
              />
            </FormGroup>
          </FormControl>
        </Grid>

        <Grid item xs>
          <FormControl component="fieldset">
            <FormGroup style={{ width: 225 }}>
              <FormControlLabel
                control={<Checkbox checked={this.state.giftCard} />}
                label="Add Gift Card"
                onChange={this.handleGiftCard}
              />
              <TextField
                type="number"
                disabled={this.state.giftCardBalanceTextField}
                style={{ width: 180 }}
                label="Gift Card Initial Balance"
                variant="outlined"
                size="small"
                value={this.state.giftCardBalance}
                inputProps={{
                  style: { color: this.state.giftCardBalanceTextColor },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                onChange={this.handleGiftCardBalance}
              />
            </FormGroup>
          </FormControl>
        </Grid>

        {!this.props.isAmazonCustomer && (
          <Grid
            item
            xs={12}
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={3}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={this.state.rxPassSubscription}
                  onChange={this.handleRxPass}
                />
              }
              label="Add RxPass Subscription"
            />
          </Grid>
        )}

        {!this.props.isAmazonCustomer && (
          <Grid item xs>
            <FormControl component="fieldset">
              <FormGroup style={{ width: 225 }}>
                <FormControlLabel
                  control={<Checkbox checked={this.state.rxPassForceActive} />}
                  label="Enable RxPass ForceActive"
                  onChange={this.handleRxPassForceActive}
                />
              </FormGroup>
            </FormControl>
          </Grid>
        )}

        {!this.props.isAmazonCustomer && this.props.isDemographicProfile && (
          <Grid
            item
            xs={12}
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={3}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={this.state.hornbillSubscription}
                  onChange={this.handleHornbillSubscription}
                />
              }
              label="Add Hornbill Subscription"
            />
          </Grid>
        )}
      </Grid>
    );
  }

  public handlePrime(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.checked) {
      this.setState({ prime: true });
      this.setState({ creditCard: true });
    } else {
      this.setState({ prime: false });
    }

    if (this.state.rxPassSubscription && !event.target.checked) {
      alert(
        "Prime Subscription should be enabled to create RxPass pharmacy customer"
      );
    }
  }

  public handleRxPass(event: React.ChangeEvent<HTMLInputElement>) {
    if (!this.state.prime) {
      alert(
        "RxPass subscription is available for prime pharmacy customers only. Please check the prime checkbox."
      );
    }

    if (this.state.prime && event.target.checked) {
      this.setState({ rxPassSubscription: true });
    } else {
      this.setState({ rxPassSubscription: false });
    }

    if (this.state.rxPassSubscription && !event.target.checked) {
      alert(
        "RxPass Subscription should be enabled to force active the rx pass."
      );
    }
  }

  public handleRxPassForceActive(event: React.ChangeEvent<HTMLInputElement>) {
    if (!this.state.rxPassSubscription) {
      alert(
        "RxPass force active is available for RxPass pharmacy customers only. Please check the RxPass subscription checkbox."
      );
    }

    if (this.state.rxPassSubscription && event.target.checked) {
      this.setState({ rxPassForceActive: true });
    } else {
      this.setState({ rxPassForceActive: false });
    }
  }

  public handleCreditCard(event: React.ChangeEvent<HTMLInputElement>) {
    if (this.state.prime) {
      alert(
        "Prime Membership requires a credit card to be added. \r\n" +
          "Please make sure you don't want a Prime Membership either."
      );
    }

    if (event.target.checked) {
      this.setState({ creditCard: true });
    } else {
      this.setState({ creditCard: false });
      this.setState({ prime: false });
    }
  }

  public handleGiftCard(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.checked) {
      this.setState({ giftCard: true });
      this.setState({ giftCardBalanceTextField: false });
      this.setState({ giftCardBalanceTextColor: "blue" });
    } else {
      this.setState({ giftCard: false });
      this.setState({ giftCardBalanceTextField: true });
      this.setState({ giftCardBalanceTextColor: "gray" });
    }
  }

  public handleGiftCardBalance(event: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ giftCardBalance: event.target.value });
  }

  public handleName(event: React.ChangeEvent<HTMLTextAreaElement>) {
    this.setState({ name: event.target.value });
  }

  public handleEmailPrefix(event: React.ChangeEvent<HTMLTextAreaElement>) {
    this.setState({ emailPrefix: event.target.value });
  }

  public handlePhoneNumber(event: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = event.target.value.replace(/\D/g, "").slice(-10);
    this.setState({ phone: value });
  }

  public handleHornbillSubscription(event: React.ChangeEvent<HTMLInputElement>) {
    if (!this.state.prime) {
      alert(
        "Hornbill subscription is available for prime pharmacy customers only. Please check the prime checkbox."
      );
    }

    if (this.state.prime && event.target.checked) {
      this.setState({ hornbillSubscription: true });
    } else {
      this.setState({ hornbillSubscription: false });
    }

  }

  public handleResidentialAddressId(
    event: React.ChangeEvent<{ value: unknown }>
  ) {
    this.setState({ residentialAddressId: event.target.value as string });
  }

  public getState() {
    return this.state;
  }
}

export default BasicFieldsSection;
